<script setup lang="ts">
const page = {
  title: 'Pricing - Cronr',
  description: 'Choose the plan that works best for you',
  navigation: {
    icon: 'i-lucide-credit-card'
  },
  hero: {
    title: 'A plan for every need',
    description: 'Choose the plan that works best for you',
    links: []
  },
  plans: [
    {
      title: 'Hobby',
      description: 'Perfect for side projects and small applications',
      price: {
        month: '$9.99',
        year: '$99.9'
      },
      features: [
        'Up to 10 cron jobs',
        'Email notifications',
        'Basic monitoring',
        'Community support'
      ],
      button: {
        label: 'Get Started',
        to: '/signup',
        color: 'neutral' as const,
        variant: 'subtle' as const
      }
    },
    {
      title: 'Pro',
      description: 'For growing businesses and teams',
      price: {
        month: '$19.99',
        year: '$199.9'
      },
      features: [
        'Up to 50 cron jobs',
        'Email & Slack notifications',
        'Advanced monitoring',
        'Priority support',
        'Team collaboration'
      ],
      button: {
        label: 'Get Started',
        to: '/signup',
        color: 'primary' as const
      },
      highlight: true,
      scale: true
    },
    {
      title: 'Enterprise',
      description: 'For large organizations with custom needs',
      price: {
        month: '$99.99',
        year: '$999.9'
      },
      features: [
        'Unlimited cron jobs',
        'All notification channels',
        'Custom monitoring',
        'Dedicated support',
        'Advanced security',
        'Custom integrations'
      ],
      button: {
        label: 'Get Started',
        to: '/signup',
        color: 'neutral' as const,
        variant: 'subtle' as const
      }
    }
  ],
  logos: {
    icons: [
      'i-simple-icons-github',
      'i-simple-icons-docker',
      'i-simple-icons-kubernetes',
      'i-simple-icons-amazonaws',
      'i-simple-icons-googlecloud',
      'i-simple-icons-microsoftazure'
    ]
  },
  faq: {
    title: 'Frequently asked questions',
    description: 'Have a question? Check out our FAQ below.',
    items: [
      {
        label: 'What is Cronr?',
        content: 'Cronr is a monitoring service for your cron jobs. It helps you ensure your scheduled tasks are running correctly and notifies you when something goes wrong.'
      },
      {
        label: 'How does billing work?',
        content: 'We offer monthly and yearly billing. You can cancel or change your plan at any time. Yearly plans come with a 17% discount.'
      },
      {
        label: 'Do you offer a free trial?',
        content: 'Yes, you can try Cronr for free for 14 days. No credit card required.'
      },
      {
        label: 'What payment methods do you accept?',
        content: 'We accept all major credit cards and PayPal.'
      }
    ]
  }
}

useSeoMeta({
  title: page.title,
  ogTitle: page.title,
  description: page.description,
  ogDescription: page.description
})

const isYearly = ref('0')

const items = ref([
  {
    label: 'Monthly',
    value: '0'
  },
  {
    label: 'Yearly',
    value: '1'
  }
])
</script>

<template>
  <div>
    <UPageHero v-bind="page.hero">
      <template #links>
        <UTabs
          v-model="isYearly"
          :items="items"
          color="neutral"
          class="w-72"
          :ui="{ list: 'rounded-full', indicator: 'rounded-full' }"
        />
      </template>
    </UPageHero>

    <UContainer>
      <UPricingPlans scale>
        <UPricingPlan
          v-for="(plan, index) in page.plans"
          :key="index"
          v-bind="plan"
          :price="isYearly === '1' ? plan.price.year : plan.price.month"
          :billing-cycle="isYearly === '1' ? '/year' : '/month'"
        />
      </UPricingPlans>
    </UContainer>

    <UPageSection>
      <UPageLogos>
        <UIcon
          v-for="icon in page.logos.icons"
          :key="icon"
          :name="icon"
          class="w-12 h-12 flex-shrink-0 text-(--ui-text-muted)"
        />
      </UPageLogos>
    </UPageSection>

    <UPageSection
      :title="page.faq.title"
      :description="page.faq.description"
    >
      <UPageAccordion
        :items="page.faq.items"
        multiple
        class="max-w-4xl mx-auto"
      />
    </UPageSection>
  </div>
</template>
